import {
  Button,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Table,
  Typography,
} from "antd";
import type { PaginationProps } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/rootReducer";
import {
  deleteMasterData,
  deleteSalesFile,
  getMasterData,
} from "../../redux/actions/salesDataActions";
import {
  Actions,
  ExcludedMasterKeys,
  PageSize,
  RawDataType,
} from "../../shared/constant";
import MasterDataEditModal from "./MasterDataEditModal";
import Icon from "../Icon/Icon";
import globalUtils from "../../utils/globalUtils";
import SearchComponent from "../_shared/SearchComponent";

const { Option } = Select;
interface Item {
  [key: string]: string;
}

// TODO take this into tableUtils;
const buildColumns = (
  masterData: object[],
  onEdit: Function,
  onDelete: Function,
  selectedRowKeys: any,
) => {
  const columns: any[] = [];

  const keys = masterData.length ? Object.keys(masterData[0]) : [];
  const allowedKeys = keys.filter((item) => !ExcludedMasterKeys.includes(item));
  const headers = allowedKeys.map((item) =>
    globalUtils.snakeCaseToCapitalize(item),
  );

  const handleEdit = (record: Item) => {
    onEdit(record);
  };

  const handleDelete = (record: Item) => {
    onDelete(record);
  };

  const isDisable = (id: string) => {
    if (selectedRowKeys?.includes(id)) return true;
    return false;
  };

  allowedKeys.forEach((key: string, index: number) => {
    columns.push({
      title: headers[index],
      dataIndex: key,
      key,
      editable: true,
      render: (value: string, record: any) => {
        if (index === 0 && !record.is_valid_data) {
          return (
            <div className="flex items-center gap-x-[9px]">
              <Icon icon="warning" className="w-[18px] h-[14px]" />
              <div>{value}</div>
            </div>
          );
        }

        return <div>{value}</div>;
      },
    });
  });

  if (allowedKeys.length) {
    columns.push({
      title: "Actions",
      key: "action",
      fixed: "right",
      render: (_: any, record: Item) => (
        <div className="flex gap-2.5">
          <Typography.Link
            disabled={isDisable(record.id)}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Typography.Link>
          <Popconfirm
            title="Are you sure to delete?"
            onConfirm={() => {
              handleDelete(record);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Typography.Link disabled={isDisable(record.id)}>
              Delete
            </Typography.Link>
          </Popconfirm>
        </div>
      ),
    });
  }

  return columns;
};

// function TooltipRow(props: any) {
//   console.log("Props: ", props);
//   return (
//     <Tooltip
//       placement="topLeft"
//       overlayClassName="max-w-[370px]"
//       title="Invalid Data. Please correct the data before processing"
//     >
//       <tr {...props} />
//     </Tooltip>
//   );
// }

const MasterDataView = ({ fileID }: { fileID: string }) => {
  const dispatch = useDispatch();
  const { masterData, masterDataResponse, fulfilled } = useSelector(
    (state: RootState) => state.salesData,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState(false);
  const [rowData, setRowData] = useState<Item | null>(null);
  const [searchText, setSearchText] = useState("");
  const [validity, setValidity] = useState("");
  const [action, setAction] = useState("");
  const [effect, setEffect] = useState(false);

  const handlePageChange: PaginationProps["onChange"] = (p) => {
    setPage(p);
  };

  useEffect(() => {
    dispatch(
      dispatch(
        getMasterData({
          file_id: fileID,
          page,
          search: searchText,
          is_valid_data: validity,
        }),
      ),
    );
  }, [page, effect, validity]);

  useEffect(() => {
    // console.log("FULFILLED", action);

    if (
      action === Actions.EDIT ||
      action === Actions.DELETE ||
      action === Actions.MULTI_DELETE
    ) {
      if (action === Actions.EDIT)
        message.success("Record edited successfully");
      if (action === Actions.DELETE) {
        if (masterData.length === 1 && page > 1) setPage(page - 1);
        message.success("Record deleted successfully");
      }
      if (action === Actions.MULTI_DELETE) {
        setPage(1);
        message.success("Records deleted successfully");
      }
      setEffect(!effect);
      setAction("");
    }
  }, [fulfilled]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
    setEffect(!effect);
  };

  const onEdit = (record: Item) => {
    setVisible(true);
    setRowData(record);
    setAction(Actions.EDIT);
  };

  const onDelete = (record: Item) => {
    dispatch(deleteMasterData(record.id));
    setAction(Actions.DELETE);
  };

  const onValidityChange = (value: string) => {
    if (value) setValidity(value);
    else setValidity("");
    setPage(1);
    setEffect(!effect);
  };

  const deleteSelected = () => {
    Modal.confirm({
      title: "Are you sure to delete these records?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      cancelText: "No",
      centered: true,
      onOk: async () => {
        await dispatch(deleteSalesFile({ ids: selectedRowKeys }));
        setAction(Actions.MULTI_DELETE);
        setSelectedRowKeys([]);
      },
      onCancel() {},
    });
    // setEffect(!effect);
  };

  const hideModal = () => {
    setVisible(false);
    setRowData(null);
    setAction("");
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // useEffect(() => {
  //   console.log(selectedRowKeys);
  // }, [selectedRowKeys]);

  return (
    <div>
      <div className="flex items-center gap-x-6 mb-4">
        <div className="flex items-end gap-x-2.5">
          <div className="text-heading21 text-sea-green">
            {masterDataResponse?.valid_count}
          </div>
          <div>Valid Rows</div>
        </div>
        <div className="flex items-end gap-x-2.5">
          <div className="text-heading21 text-metallic-orange">
            {masterDataResponse?.invalid_count}
          </div>
          <div>Invalid Rows</div>
        </div>
      </div>
      <div className="mt-5 mb-4 space-x-4">
        <SearchComponent
          handleSearch={handleSearch}
          disabled={!!selectedRowKeys.length}
        />
        <Select
          placeholder="Validity"
          className="w-[190px]"
          allowClear
          onChange={onValidityChange}
          disabled={!!selectedRowKeys.length}
        >
          {RawDataType.map((item) => (
            <Option key={item.value} value={item.value}>
              {item.label}
            </Option>
          ))}
        </Select>
        {selectedRowKeys.length > 0 && (
          <Button type="primary" danger onClick={deleteSelected}>
            Delete
          </Button>
        )}
      </div>
      <Table
        rowSelection={masterData.length && rowSelection}
        rowKey={(record: any) => {
          return record.id;
        }}
        dataSource={masterData}
        columns={buildColumns(masterData, onEdit, onDelete, selectedRowKeys)}
        rowClassName={(record) =>
          !record.is_valid_data ? "invalid-table-row" : ""
        }
        // components={{
        //   body: {
        //     row: TooltipRow,
        //   },
        // }}
        pagination={false}
        size="middle"
        // scroll={{ x: 1300 }}
        scroll={{ ...(globalUtils.checkIsScroll(masterData) && { y: 500 }) }}
        className="ant_table"
      />
      <div className="text-right mt-4">
        <Pagination
          current={page}
          pageSize={PageSize.FIFTY}
          onChange={handlePageChange}
          total={masterDataResponse?.count}
          showSizeChanger={false}
        />
      </div>
      <MasterDataEditModal
        visible={visible}
        editData={rowData as Item}
        onCancel={hideModal}
      />
    </div>
  );
};

export default MasterDataView;
